import React, { useEffect, useReducer } from 'react'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, createSearchParams } from 'react-router-dom'

import { ProtectedPage } from '~/src/components/templates'
import { TableNotifications } from '~/src/components/molecules'
import { AddNewButton } from '~/src/components/atoms/Button/AddNewButton/AddNewButton'
import { useNotifications } from '~/src/hooks/useApi'
import { useMessage } from '~/src/hooks'
import { LoadingNormal } from '~/src/components/organisms'
import { notificationRepository } from '~/src/repositories'
import useSWR from 'swr'

type Params = {
  limit: number
  offset?: number
}

enum ActionType {
  SET_OFFSET,
}

type Action = {
  type: ActionType
  payload: Partial<Params>
}

const reducer = (state: Params, action: Action): Params => {
  switch (action.type) {
    case ActionType.SET_OFFSET:
      return { ...state, offset: action.payload.offset }
  }
}


/**
 * NotificationList Page
 *
 * @param __namedParameters
 * @returns JSX.Element
 * @category ReactComponents
 */
export const NotificationList: React.VFC = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { handleOpenWithErrorMessage } = useMessage()

  const initState: Params = (
    // @ts-ignore
    location.state && location.state.prevState
    ? // @ts-ignore
      (location.state.prevState as Params)
    : location.state
    ? (location.state as Params)
    : ({ limit: 20 } as Params)
  )
  const [params, dispatch] = useReducer(reducer, initState)
  const { data, error } = useSWR(['/notifications', params], () =>
    notificationRepository.getNotifications(params)
  )

  const handleAddNew = () => {
    navigate('/admin/news/add', {
      state: {
        prevLocationState: { ...location }
      }
    })
  }

  const fetchNotification = async (_params?: Params) => {
    let mergedParams = _params
    if (location.state) {
      // @ts-ignore
      mergedParams = { ...location.state, ..._params }
    }
    // @ts-ignore
    dispatch({ type: ActionType.SET_OFFSET, payload: { offset: mergedParams?.offset } })
    navigate(
      {
        pathname: location.pathname,
        // @ts-ignore
        search: `?${createSearchParams(mergedParams)}`,
      },
      // @ts-ignore
      { state: mergedParams }
    )
  }

  useEffect(() => {
    if (error) handleOpenWithErrorMessage(error.message)
  }, [error])

  return (
    <ProtectedPage pageTitle={t('page_title.news_list')}>
      <AddNewButton text={'notification.add'} handleAddNew={handleAddNew} />
      <Box paddingTop={3}>
        {!data ? (
          <LoadingNormal />
        ) : (
          <TableNotifications
            notifications={data.data.notifications}
            pagination={data.data.pagination}
            onPageChange={fetchNotification}
          />
        )}
      </Box>
    </ProtectedPage>
  )
}
